/**
 * O "VOLTAR" DO ANDROID FECHA A JANELA ABERTA (30/09/2026, item 7 do pedido
 * do Leandro sobre o portal do mestre).
 *
 * No celular (PWA e TWA) o voltar do Android navegava para a tela anterior
 * mesmo com uma janela aberta: o mestre saía da obra e perdia o que tinha
 * preenchido. Agora, com janela, folha, menu ou o visualizador de fotos
 * aberto, o voltar fecha o de cima — como o "‹" ou o × dele. A janela com
 * algo preenchido pergunta antes, como sempre.
 *
 * COMO: o `CloseWatcher` do Chrome, que recebe o voltar do Android como um
 * "pedido de fechar". O histórico fica INTOCADO. Empilhar uma entrada por
 * janela e chamar `history.back()` ao fechar faria o roteador do Next
 * "restaurar" a página, e a fila de ações dele descarta a ação pendente
 * quando chega um restore. Sairia fora a navegação ou o `router.refresh()`
 * que o salvar acabou de disparar. Sem janela aberta, o voltar segue voltando
 * de tela.
 *
 * UM VIGIA SÓ, na raiz do app, para todas as janelas: as do design system e
 * as antigas. Todas são o Dialog/AlertDialog/Popover/DropdownMenu do Radix,
 * montadas direto no <body>, e todas já fecham a camada de cima pelo Esc, com
 * as regras delas (a confirmação de descarte, o bloqueio enquanto salva). O
 * voltar vira um Esc na camada de cima. A camada que não é do Radix (o
 * visualizador de fotos), ou que volta um passo em vez de fechar (a folha com
 * "‹"), se registra com o próprio fechar (`registerBackCloser`).
 *
 * UM CloseWatcher POR VEZ: o navegador agrupa os que nascem sem toque do
 * usuário, e um voltar fecharia o grupo inteiro. Depois de cada voltar, se
 * ainda há camada aberta, o vigia arma outro.
 *
 * Sem `CloseWatcher` (iPhone, navegador antigo) ou sem tela de toque, nada
 * muda: o iPhone não tem voltar, e no computador o Esc já fecha.
 *
 * Funções puras + um registro em memória. O componente
 * `components/layout/BackButtonCloses` liga isto ao DOM.
 */

/**
 * As camadas abertas que o Radix monta direto no <body>: janela e folha
 * (`role=dialog`), confirmação (`role=alertdialog`) e o que abre preso a um
 * botão (menu "…", popover, seleção), dentro do invólucro do Popper. O
 * `data-state="open"` deixa de fora a camada que está saindo (animação de
 * fechar) e o tooltip ("delayed-open").
 */
export const OPEN_LAYER_SELECTOR = [
  'body > [role="dialog"][data-state="open"]',
  'body > [role="alertdialog"][data-state="open"]',
  'body > [data-radix-popper-content-wrapper] > [data-state="open"]',
].join(", ");

/** O pedaço do `CloseWatcher` que o vigia usa. */
export type WatcherLike = {
  onclose: (() => void) | null;
  destroy(): void;
};

export type BackCloserEnv<L> = {
  /** A camada aberta de cima, ou `null` sem camada aberta. */
  topLayer(): L | null;
  /** Um vigia novo, ou `null` quando o navegador não tem. */
  createWatcher(): WatcherLike | null;
  /** Fecha a camada: o fechar registrado dela, senão um Esc. */
  close(layer: L): void;
  /** Adia uma conferência. O Esc pode não fechar (a janela salvando). */
  defer(fn: () => void): void;
};

export type BackCloser = {
  /** Confere as camadas abertas e arma ou desarma o vigia. */
  sync(): void;
  dispose(): void;
  /** Há vigia armado? (testes) */
  readonly armed: boolean;
};

export function createBackCloser<L>(env: BackCloserEnv<L>): BackCloser {
  let watcher: WatcherLike | null = null;
  let disposed = false;

  function disarm() {
    const w = watcher;
    watcher = null;
    if (w) {
      w.onclose = null;
      w.destroy();
    }
  }

  function sync() {
    if (disposed) return;
    const open = env.topLayer() !== null;
    if (open && !watcher) {
      const w = env.createWatcher();
      if (!w) return;
      watcher = w;
      w.onclose = () => {
        // O navegador já destruiu este vigia: só esquece.
        if (watcher === w) watcher = null;
        const top = env.topLayer();
        if (top !== null) env.close(top);
        env.defer(sync);
      };
    } else if (!open && watcher) {
      disarm();
    }
  }

  return {
    sync,
    dispose() {
      disposed = true;
      disarm();
    },
    get armed() {
      return watcher !== null;
    },
  };
}

/**
 * A de cima entre várias camadas: a última na ordem do documento. As do Radix
 * entram no <body> na ordem em que abrem, e a camada de dentro de outra (o
 * visualizador de fotos aberto numa janela) vem depois da de fora.
 * `follows(a, b)` diz se `a` vem depois de `b`.
 */
export function lastInOrder<L>(
  layers: Iterable<L>,
  follows: (a: L, b: L) => boolean,
): L | null {
  let top: L | null = null;
  for (const layer of layers) {
    if (top === null || follows(layer, top)) top = layer;
  }
  return top;
}

// ─── Registro das camadas com fechar próprio ────────────────────────────────

const closers = new Map<object, () => void>();
const listeners = new Set<() => void>();

function notify() {
  for (const fn of listeners) fn();
}

/**
 * Registra uma camada com o fechar dela. O voltar chama esse fechar em vez do
 * Esc. Serve para a camada que não é do Radix (o visualizador de fotos) e
 * para a que volta um passo (a folha com "‹"). Devolve o cancelamento.
 */
export function registerBackCloser(layer: object, close: () => void) {
  closers.set(layer, close);
  notify();
  return () => {
    if (closers.get(layer) === close) closers.delete(layer);
    notify();
  };
}

/** O fechar registrado de uma camada. */
export function backCloserOf(layer: object): (() => void) | undefined {
  return closers.get(layer);
}

/** As camadas registradas (as abertas; quem sai cancela o registro). */
export function registeredLayers(): object[] {
  return [...closers.keys()];
}

/** Avisa o vigia quando o registro muda. Devolve o cancelamento. */
export function subscribeBackClosers(fn: () => void) {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}
